import { Badge, Checkbox, List, ListRow, todayIso, useToast } from '@ui';
import { Fragment, useState, type ReactNode } from 'react';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { useRowMenu } from '../../../app/rowActions';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useSession } from '../../identity/session';
import { isQueued } from '../../offline/offline';
import { useSaveTask, useTaskAction, type Task } from '../api';
import { TaskFormDialog } from './TaskFormDialog';
import styles from './tasks.module.css';

export interface TaskListProps {
  tasks: Task[];
  /** Hide references that are the context anyway (e.g. on a customer page). */
  hideRefs?: boolean;
}

/** An ISO date `days` after `iso`. */
function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

/** Monday of next week. */
function nextMonday(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const weekday = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
  return addDays(iso, 7 - weekday);
}

/** Postponing only moves a task later, never earlier (#201). */
function postponesTo(task: Task, dueOn: string): boolean {
  return !task.due_on || dueOn > task.due_on;
}

/** Tasks like iOS reminders: checkbox, title, grey due line (TSK-001, TSK-003). */
export function TaskList({ tasks, hideRefs = false }: TaskListProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useSession();
  const action = useTaskAction();
  const write = can('tasks.write');
  const save = useSaveTask();
  const rowMenu = useRowMenu();
  const [editing, setEditing] = useState<Task | null>(null);
  const [deleting, setDeleting] = useState<Task | null>(null);

  // Postponing keeps everything else of the task; it can be undone.
  const postpone = (task: Task, dueOn: string) => {
    const body = (due: string | null) => ({
      title: task.title,
      description: task.description ?? null,
      due_on: due,
      due_time: due ? (task.due_time ?? null) : null,
      reminder: task.reminder ?? null,
      assignee_id: task.assignee?.id ?? null,
      priority: task.priority,
      recurrence: task.recurrence ?? null,
      colony_ids: task.colonies.map((c) => c.id),
      location_id: task.location?.id ?? null,
      customer_id: task.customer?.id ?? null,
      sale_id: task.sale?.id ?? null,
    });
    save.mutate(
      { id: task.id, version: task.version, body: body(dueOn) },
      {
        onSuccess: (saved) =>
          toast.show(t('tasks.list.postponed', { date: formatDate(dueOn) }), 'success', {
            label: t('common.undo'),
            onClick: () =>
              save.mutate(
                { id: task.id, version: saved.version, body: body(task.due_on ?? null) },
                { onError: (err) => toast.show(errorMessage(err), 'danger') },
              ),
          }),
        onError: (err) => toast.show(errorMessage(err), 'danger'),
      },
    );
  };

  // Completing is reversible: undo right away, except for recurring tasks
  // whose next occurrence has already been created (§6.10).
  const toggle = (task: Task) => {
    const done = task.status === 'done';
    action.mutate(
      { id: task.id, action: done ? 'reopen' : 'complete' },
      {
        onSuccess: (result) => {
          // Captured offline: one toast, and no undo since nothing happened yet (#190).
          if (isQueued(result)) {
            toast.show(t('offline.captured'), 'info');
            return;
          }
          toast.show(
            done
              ? t('tasks.list.reopened')
              : task.recurrence
                ? t('tasks.list.completedNext')
                : t('tasks.list.completed'),
            'success',
            !done && !task.recurrence
              ? {
                  label: t('common.undo'),
                  onClick: () =>
                    action.mutate(
                      { id: task.id, action: 'reopen' },
                      { onError: (err) => toast.show(errorMessage(err), 'danger') },
                    ),
                }
              : undefined,
          );
        },
        onError: (err) => toast.show(errorMessage(err), 'danger'),
      },
    );
  };

  return (
    <>
      <List>
        {tasks.map((task) => {
          const done = task.status === 'done';
          const tomorrow = addDays(todayIso(), 1);
          const nextWeek = nextMonday(todayIso());
          // The priority leads the grey line, not the right edge (#60).
          const facts: { key: string; node: ReactNode }[] = [
            ...(task.priority === 'high' && !done
              ? [
                  {
                    key: 'priority',
                    node: (
                      <Badge size="sm" tone="warning">
                        {t('tasks.priority.high')}
                      </Badge>
                    ),
                  },
                ]
              : []),
            ...(task.due_on
              ? [
                  {
                    key: 'due',
                    node: (
                      <span className={task.overdue && !done ? styles.overdue : undefined}>
                        {task.overdue && !done
                          ? t('tasks.list.overdue', { date: formatDate(task.due_on) })
                          : formatDate(task.due_on)}
                        {task.due_time && `, ${task.due_time.slice(0, 5)}`}
                      </span>
                    ),
                  },
                ]
              : []),
            ...(task.recurrence
              ? [
                  {
                    key: 'recurrence',
                    node: (
                      <span>
                        {task.recurrence.every === 1
                          ? t(`tasks.recurrence.${task.recurrence.unit}`)
                          : t('tasks.list.every', {
                              count: task.recurrence.every,
                              unit: t(`tasks.unit.${task.recurrence.unit}`),
                            })}
                      </span>
                    ),
                  },
                ]
              : []),
            ...(task.remind_at && !done
              ? [{ key: 'reminder', node: <span>{t('tasks.list.reminder')}</span> }]
              : []),
            ...(hideRefs
              ? []
              : [
                  ...task.colonies.map((c) => ({ key: c.id, name: c.name })),
                  ...(task.location ? [{ key: task.location.id, name: task.location.name }] : []),
                  ...(task.customer ? [{ key: task.customer.id, name: task.customer.name }] : []),
                  ...(task.sale ? [{ key: task.sale.id, name: task.sale.name }] : []),
                ].map((r) => ({ key: r.key, node: <span>{r.name}</span> }))),
            ...(done && task.done_by
              ? [
                  {
                    key: 'doneBy',
                    node: <span>{t('tasks.list.doneBy', { name: task.done_by })}</span>,
                  },
                ]
              : []),
          ];
          return (
            <ListRow
              key={task.id}
              href={`/aufgaben/${task.id}`}
              leading={
                <label className={styles.check} htmlFor={`task-check-${task.id}`}>
                  <Checkbox
                    id={`task-check-${task.id}`}
                    aria-label={
                      done
                        ? t('tasks.list.reopen', { title: task.title })
                        : t('tasks.list.complete', { title: task.title })
                    }
                    checked={done}
                    disabled={!write || action.isPending}
                    onChange={() => toggle(task)}
                  />
                </label>
              }
              title={done ? <span className={styles.done}>{task.title}</span> : task.title}
              description={
                facts.length > 0 &&
                facts.map((f, i) => (
                  <Fragment key={f.key}>
                    {i > 0 && ' · '}
                    {f.node}
                  </Fragment>
                ))
              }
              trailing={task.assignee?.name}
              actionsLabel={rowMenu.label(task.title)}
              actions={
                write
                  ? [
                      {
                        key: 'toggle',
                        label: done ? t('tasks.list.markOpen') : t('tasks.list.markDone'),
                        onSelect: () => toggle(task),
                      },
                      !done &&
                        postponesTo(task, tomorrow) && {
                          key: 'tomorrow',
                          label: t('tasks.list.postponeTomorrow'),
                          onSelect: () => postpone(task, tomorrow),
                        },
                      !done &&
                        postponesTo(task, nextWeek) && {
                          key: 'nextWeek',
                          label: t('tasks.list.postponeNextWeek'),
                          onSelect: () => postpone(task, nextWeek),
                        },
                      { ...rowMenu.edit(() => setEditing(task)), separated: true },
                      rowMenu.remove(() => {
                        action.reset();
                        setDeleting(task);
                      }),
                    ]
                  : []
              }
            />
          );
        })}
      </List>
      {editing && <TaskFormDialog open task={editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          action.reset();
          setDeleting(null);
        }}
        title={t('tasks.detail.deleteTitle', { title: deleting?.title ?? '' })}
        description={deleting?.recurrence ? t('tasks.detail.deleteText') : undefined}
        confirmLabel={t('common.delete')}
        pending={action.isPending}
        error={action.error ? errorMessage(action.error) : undefined}
        onConfirm={() =>
          deleting &&
          action.mutate(
            { id: deleting.id, action: 'delete' },
            {
              onSuccess: () => {
                toast.show(t('tasks.detail.deleted'));
                setDeleting(null);
              },
            },
          )
        }
      />
    </>
  );
}
